import { datadogRum } from "@datadog/browser-rum";

// Datadog's auto-captured viewport sometimes misreports on dual-screen and
// foldable Android (e.g. Surface Duo, Galaxy Z Fold). This module attaches
// every relevant raw native dimension to RUM as global context, and emits an
// action on mount / resize / orientationchange so we can cross-check what
// Datadog auto-detects against ground truth at the moment a layout problem
// appears.
//
// Lives outside services/datadog.ts because jest.config moduleNameMapper
// redirects services/datadog imports to a mock in tests, which would prevent
// the real implementation from being tested.

type ViewportContext = Record<string, string | number | boolean | null>;

const safeMatchMedia = (query: string): boolean => {
	try {
		return globalThis.matchMedia(query).matches;
	} catch {
		return false;
	}
};

const collectViewportContext = (): ViewportContext => ({
	viewport_inner_w: globalThis.innerWidth,
	viewport_inner_h: globalThis.innerHeight,
	doc_client_w: document.documentElement.clientWidth,
	doc_client_h: document.documentElement.clientHeight,
	screen_w: globalThis.screen?.width ?? -1,
	screen_h: globalThis.screen?.height ?? -1,
	screen_avail_w: globalThis.screen?.availWidth ?? -1,
	screen_avail_h: globalThis.screen?.availHeight ?? -1,
	dpr: globalThis.devicePixelRatio,
	orientation_type: globalThis.screen?.orientation?.type ?? null,
	orientation_angle: globalThis.screen?.orientation?.angle ?? null,
	ua: navigator.userAgent,
	ua_mobile:
		(navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData?.mobile ??
		null,
	spanning_vertical: safeMatchMedia("(spanning: single-fold-vertical)"),
	spanning_horizontal: safeMatchMedia("(spanning: single-fold-horizontal)"),
	h_viewport_segments_2: safeMatchMedia("(horizontal-viewport-segments: 2)"),
	v_viewport_segments_2: safeMatchMedia("(vertical-viewport-segments: 2)"),
});

const publishViewportContext = (label: "mount" | "resize" | "orientationchange"): void => {
	const ctx = collectViewportContext();

	for (const [key, value] of Object.entries(ctx)) {
		datadogRum.setGlobalContextProperty(`raw_${key}`, value);
	}

	datadogRum.addAction(`viewport.${label}`, ctx);
};

export const trackRawViewport = (): void => {
	publishViewportContext("mount");

	globalThis.addEventListener("resize", () => publishViewportContext("resize"));
	globalThis.addEventListener("orientationchange", () =>
		publishViewportContext("orientationchange")
	);
};
